/** Human-readable labels for permission slugs stored on roles.permissions JSON */
export const PERMISSION_LABELS = {
  manage_schools: 'Manage schools (platform)',
  manage_school: 'Manage school settings',
  manage_users: 'Manage users',
  manage_students: 'Manage students',
  manage_teachers: 'Manage teachers',
  manage_teacher_attendance: 'Manage teacher attendance',
  manage_fees: 'Manage fees',
  manage_academics: 'Manage academics',
  manage_class_teachers: 'Assign class teachers',
  view_school: 'View school overview',
  view_students: 'View students',
  manage_attendance: 'Manage attendance',
  manage_biometric: 'Manage biometric attendance',
  manage_grades: 'Manage grades',
  view_exams: 'View exams',
  manage_exams: 'Manage exams',
  manage_assignments: 'Manage assignments',
  view_own_data: 'View own profile & data',
  view_assignments: 'View assignments',
  submit_assignments: 'Submit assignments',
  view_results: 'View results',
  view_reports: 'View reports & documents',
  manage_reports: 'Manage reports (bulk download, templates)',
  view_child_data: 'View child data',
  view_fees: 'View fees',
  view_attendance: 'View attendance',
  view_portal_calendar: 'Portal calendar & events',
  view_portal_messages: 'Portal messages (chat)',
  manage_library: 'Manage library',
  manage_transport: 'Manage transport',
  manage_inventory: 'Manage inventory',
  approve_leave: 'Approve leave (class teacher step)',
  manage_leave: 'Final leave approval (admin queue)',
  apply_leave: 'Apply for leave',
  view_reception: 'View reception module',
  manage_reception: 'Manage reception records',
  delete_reception: 'Delete reception records',
  view_reception_reports: 'View reception reports & analytics',
  convert_reception_inquiry: 'Convert inquiry to student admission',
  software_dashboard: 'Software admin dashboard',
  enquiries: 'Manage enquiries',
};

export const PERMISSION_GROUPS = [
  {
    label: 'Administration',
    keys: ['manage_school', 'manage_users'],
  },
  {
    label: 'Admission',
    keys: ['manage_students', 'view_students'],
  },
  {
    label: 'HR',
    keys: ['manage_teachers', 'manage_teacher_attendance'],
  },
  {
    label: 'Academics',
    keys: [
      'manage_academics',
      'manage_class_teachers',
      'view_school',
      'manage_attendance',
      'manage_biometric',
      'manage_grades',
      'manage_assignments',
    ],
  },
  {
    label: 'Exams',
    keys: ['view_exams', 'manage_exams'],
  },
  {
    label: 'Reports',
    keys: ['view_reports', 'manage_reports'],
  },
  {
    label: 'Finance',
    keys: ['manage_fees', 'view_fees'],
  },
  {
    label: 'Operations',
    keys: ['manage_library', 'manage_transport', 'manage_inventory'],
  },
  {
    label: 'Portal',
    keys: ['view_own_data', 'view_assignments', 'submit_assignments', 'view_results', 'view_child_data', 'view_attendance', 'view_portal_calendar', 'view_portal_messages'],
  },
  {
    label: 'Leave',
    keys: ['approve_leave', 'manage_leave', 'apply_leave'],
  },
  {
    label: 'Reception',
    keys: ['view_reception', 'manage_reception', 'delete_reception', 'view_reception_reports', 'convert_reception_inquiry'],
  },
];

export function parseRolePermissions(role) {
  if (!role?.permissions) return [];
  if (Array.isArray(role.permissions)) return role.permissions;
  try {
    const parsed = typeof role.permissions === 'string' ? JSON.parse(role.permissions) : role.permissions;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function permissionLabel(key) {
  return PERMISSION_LABELS[key] || key.replace(/_/g, ' ');
}

/** Platform-only permissions — hidden from school settings matrix */
export const PLATFORM_PERMISSION_KEYS = ['manage_schools', 'software_dashboard', 'enquiries'];

/** Roles hidden from school settings (User Roles + Permissions) */
export const EXCLUDED_SETTINGS_ROLE_SLUGS = ['super_admin'];

export const ALL_PERMISSION_KEYS = Object.keys(PERMISSION_LABELS);

export function filterSchoolSettingsRoles(roles) {
  return (roles || []).filter((r) => !EXCLUDED_SETTINGS_ROLE_SLUGS.includes(r.slug));
}

export function isRolePermissionEditable(role) {
  return role && !EXCLUDED_SETTINGS_ROLE_SLUGS.includes(role.slug);
}

/** Roles a school admin may assign to staff users */
export const ASSIGNABLE_STAFF_ROLE_SLUGS = ['school_admin', 'principal', 'teacher', 'staff', 'receptionist'];

export const ASSIGNABLE_EXTRA_PERMISSION_KEYS = ALL_PERMISSION_KEYS.filter(
  (k) => !PLATFORM_PERMISSION_KEYS.includes(k)
);

export function getAddableExtraPermissions(rolePermissions = [], extraPermissions = []) {
  const taken = new Set([...(rolePermissions || []), ...(extraPermissions || [])]);
  return ASSIGNABLE_EXTRA_PERMISSION_KEYS.filter((k) => !taken.has(k));
}

export function normalizeRolesFromApi(roles) {
  return (roles || []).map((r) => ({
    ...r,
    permissions: parseRolePermissions(r),
  }));
}
